import {
    Box,
    Divider,
    Flex,
    Heading,
    IconButton,
    Icons,
    Image,
    ListItem,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import Price from "components/price";
import { ActivityMenuPair } from "components/selection-components/summary/activity-menu-pair";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { getLocalizedValue } from "helpers/language-helper";
import { Moment } from "moment";
import { useTranslation } from "react-i18next";
import { MenuGroup } from "types/menu-group";
import { PaymentRule } from "types/payment";

export interface SummaryEntry {
    key: string;
    name: Record<string, string>;
    imageUrl: string;
    startTime: Moment;
    /** Same as startTime for standalone activities — causes no time range to be shown */
    endTime: Moment;
    price: number;
    priceText: string | null;
    paymentRule?: PaymentRule;
    /** Inline menu items rendered in the header (standalone activities) */
    groups?: MenuGroup[];
    /** Child sections rendered below buttons; empty for standalone activities */
    children: ActivityMenuPair[];
    isExperience: boolean;
    trackAttribute: string;
    onEdit?: () => void;
    onDelete?: () => void;
}

interface SummaryRowProps {
    entry: SummaryEntry;
    isDetailsForm: boolean;
    showDivider: boolean;
}

interface MenuItemLinesProps {
    groups: MenuGroup[] | undefined;
    isExperience: boolean;
    keyPrefix: string;
}

/** The chosen items of a menu, one line per item. Prices are folded into the experience total. */
function MenuItemLines({ groups, isExperience, keyPrefix }: MenuItemLinesProps) {
    const { t } = useTranslation();

    if (!groups || groups.length === 0) return null;

    return (
        <>
            {groups.map((group, gindex) =>
                group.items?.map((item, iindex) => (
                    <Text
                        key={`${keyPrefix}g${gindex}i${iindex}`}
                        color={ThemeColorVariable.OnSurfaceSubdued}
                        fontSize={ThemeFontSizeVariable.Small}
                    >
                        {isExperience
                            ? t("activitySummary.menuItemRowNoPrice", {
                                  name: item.name,
                                  quantity: item.quantity,
                              })
                            : t("activitySummary.menuItemRow", {
                                  name: item.name,
                                  quantity: item.quantity,
                                  price: t("currency.onlyAmount", {
                                      value: item.price,
                                      display: "symbol",
                                  }),
                              })}
                    </Text>
                ))
            )}
        </>
    );
}

function SummaryRow({ entry, isDetailsForm, showDivider }: SummaryRowProps) {
    const { t } = useTranslation();
    const { data: settings } = useExternalBookingSettings();
    const hideEndTime = !!settings?.hideEndTimeForGuest;
    const {
        name,
        imageUrl,
        startTime,
        endTime,
        price,
        priceText,
        paymentRule,
        groups,
        children,
        isExperience,
        trackAttribute,
        onEdit,
        onDelete,
    } = entry;

    const localizedName = getLocalizedValue(name);
    const hasTimeRange = !hideEndTime && !startTime.isSame(endTime);
    const imageSize = 48;
    return (
        <>
            <ListItem py={ThemeSpaceVariable.Medium}>
                <Stack spacing={ThemeSpaceVariable.XSmall}>
                    <Stack
                        flexDirection="row"
                        justifyContent="space-between"
                        cursor={onEdit ? "pointer" : "default"}
                        onClick={onEdit}
                    >
                        <Stack spacing={ThemeSpaceVariable.XSmall} flex={1}>
                            <Flex alignItems="center" gap={ThemeSpaceVariable.Small}>
                                {imageUrl && (
                                    <Image
                                        src={imageUrl}
                                        alt={localizedName}
                                        width={`${imageSize}px`}
                                        height={`${imageSize}px`}
                                        borderRadius={ThemeSpaceVariable.XSmall}
                                        objectFit="cover"
                                    />
                                )}
                                <Stack spacing={ThemeSpaceVariable.None} flex={1}>
                                    <Heading
                                        as="h4"
                                        fontSize={ThemeFontSizeVariable.Medium}
                                        fontWeight="medium"
                                    >
                                        {localizedName}
                                    </Heading>
                                    <Text fontWeight="medium">
                                        {t("time.singleTime", {
                                            value: startTime.toDate().valueOf(),
                                        })}
                                        {hasTimeRange && (
                                            <>
                                                {" – "}
                                                {t("time.singleTime", {
                                                    value: endTime.toDate().valueOf(),
                                                })}
                                            </>
                                        )}
                                    </Text>
                                </Stack>
                            </Flex>
                            {paymentRule === PaymentRule.Prepayment && priceText && (
                                <Text
                                    fontSize={ThemeFontSizeVariable.Small}
                                    color={ThemeColorVariable.OnSurfaceSubdued}
                                    fontWeight="medium"
                                >
                                    {priceText}
                                </Text>
                            )}
                            {groups && groups.length > 0 && (
                                <Box>
                                    <MenuItemLines
                                        groups={groups}
                                        isExperience={isExperience}
                                        keyPrefix=""
                                    />
                                </Box>
                            )}
                        </Stack>
                        {!isDetailsForm && price > 0 && (
                            <Stack p={`${ThemeSpaceVariable.Medium} 0`} alignItems="end">
                                <Price
                                    amount={price}
                                    display="symbol"
                                    fontWeight="medium"
                                    whiteSpace="nowrap"
                                />
                            </Stack>
                        )}
                    </Stack>

                    {children.map((activity) => {
                        /**
                         * A single activity is already described by the row header above, so only
                         * its menu items are left to render. Siblings each need their own heading
                         * to tell them apart.
                         */
                        const showActivityHeading = children.length > 1;
                        if (!showActivityHeading && !activity.groups?.length) return null;

                        return (
                            <Stack
                                key={`child-${activity.bookedArticleId}`}
                                spacing={ThemeSpaceVariable.XSmall}
                            >
                                {showActivityHeading && (
                                    <Text>
                                        {activity.rule?.title || activity.sectionName}
                                        {", "}
                                        {t("time.singleTime", {
                                            value: activity.start.toDate().valueOf(),
                                        })}
                                        {!hideEndTime && (
                                            <>
                                                {" – "}
                                                {t("time.singleTime", {
                                                    value: activity.end
                                                        .clone()
                                                        .subtract(
                                                            activity.rule?.recoupTime ?? 0,
                                                            "minutes"
                                                        )
                                                        .toDate()
                                                        .valueOf(),
                                                })}
                                            </>
                                        )}
                                    </Text>
                                )}
                                <MenuItemLines
                                    groups={activity.groups}
                                    isExperience={isExperience}
                                    keyPrefix={`child-${activity.bookedArticleId}-`}
                                />
                            </Stack>
                        );
                    })}

                    {(onEdit || onDelete) && (
                        <>
                            <Flex gap={ThemeSpaceVariable.None}>
                                {onEdit && (
                                    <IconButton
                                        aria-label={t("summary.editActivityX", {
                                            activityName: localizedName,
                                        })}
                                        icon={Icons.Edit}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit();
                                        }}
                                        variant="ghost"
                                    />
                                )}
                                {onDelete && (
                                    <IconButton
                                        aria-label={t("summary.removeActivityX", {
                                            activityName: localizedName,
                                        })}
                                        icon={Icons.Delete}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete();
                                        }}
                                        variant="ghost"
                                        data-track-click={trackAttribute}
                                    />
                                )}
                            </Flex>
                        </>
                    )}
                </Stack>
            </ListItem>
            {showDivider && <Divider />}
        </>
    );
}

export { SummaryRow };
